import React from "react";
import { Icon, Button } from "./primitives.jsx";

/* Sticky top navigation with locale toggle + mobile drawer.
   `go(pageId)` and `setLocale(lc)` navigate real URLs (see main.jsx). */
export function Nav({ page, go, locale, setLocale, nav }) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navTo = (id) => { go(id); setOpen(false); };
  const glow = page === "home" && !scrolled;

  const NavLink = ({ l }) => (
    <button
      onClick={() => navTo(l.id)}
      style={{
        border: "none",
        cursor: "pointer",
        padding: "9px 14px",
        borderRadius: "var(--radius-sm)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-sm)",
        fontWeight: 600,
        color: page === l.id ? "var(--brand-red)" : "var(--text-body)",
        background: page === l.id ? "var(--red-50)" : "transparent",
        transition: "background var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out)"
      }}
      onMouseEnter={(e) => { if (page !== l.id) e.currentTarget.style.background = "var(--neutral-50)"; }}
      onMouseLeave={(e) => { if (page !== l.id) e.currentTarget.style.background = "transparent"; }}
    >
      {l.label}
    </button>
  );

  const LocaleToggle = () => (
    <div style={{ display: "inline-flex", padding: 3, gap: 2, background: "var(--neutral-100)", borderRadius: "var(--radius-pill)" }}>
      {["en", "th"].map((lc) => (
        <button
          key={lc}
          onClick={() => setLocale(lc)}
          style={{
            border: "none",
            cursor: "pointer",
            padding: "4px 12px",
            borderRadius: "var(--radius-pill)",
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.03em",
            background: locale === lc ? "var(--surface-page)" : "transparent",
            color: locale === lc ? "var(--brand-ink)" : "var(--text-muted)",
            boxShadow: locale === lc ? "var(--shadow-xs)" : "none",
            transition: "all var(--duration-base) var(--ease-out)"
          }}
        >
          {lc.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: glow ? "#fefcff" : "rgba(255,255,255,0.82)",
        backgroundImage: glow
          ? "radial-gradient(circle at 72% -40%, rgba(255, 182, 193, 0.42), transparent 62%), radial-gradient(circle at 24% 320%, rgba(173, 216, 230, 0.4), transparent 60%)"
          : "none",
        backdropFilter: "saturate(180%) blur(14px)",
        WebkitBackdropFilter: "saturate(180%) blur(14px)",
        borderBottom: scrolled ? "1px solid var(--border-subtle)" : "1px solid transparent",
        boxShadow: scrolled ? "var(--shadow-xs)" : "none",
        transition: "border-color var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out), background var(--duration-base) var(--ease-out)"
      }}
    >
      <div style={{ width: "100%", maxWidth: "var(--container-max)", margin: "0 auto", padding: "0 var(--gutter)", height: 72, display: "flex", alignItems: "center", gap: "var(--space-6)" }}>
        <button onClick={() => navTo("home")} style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}>
          <img src="/assets/logo-rework.png" height="30" alt="Rework" style={{ height: 30, width: "auto", display: "block" }} />
        </button>

        <nav className="rw-desk" style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {nav.links.map((l) => <NavLink key={l.id} l={l} />)}
        </nav>

        <div className="rw-desk" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
          <LocaleToggle />
          <Button variant="primary" size="sm" onClick={() => navTo("contact")}>{nav.getStarted}</Button>
        </div>

        <div className="rw-mob" style={{ marginLeft: "auto", display: "none", alignItems: "center", gap: 12 }}>
          <LocaleToggle />
          <button onClick={() => setOpen((o) => !o)} aria-label="Menu" aria-expanded={open} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "var(--radius-sm)", border: "1px solid var(--border-subtle)", background: "var(--surface-page)", cursor: "pointer" }}>
            <Icon name={open ? "x" : "menu"} size={20} color="var(--brand-ink)" />
          </button>
        </div>
      </div>

      {open && (
        <div className="rw-mob" style={{ borderTop: "1px solid var(--border-subtle)", background: "var(--surface-page)", padding: "var(--space-4) var(--gutter) var(--space-6)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {nav.links.map((l) => (
              <button key={l.id} onClick={() => navTo(l.id)} style={{ textAlign: "left", background: page === l.id ? "var(--red-50)" : "transparent", border: "none", cursor: "pointer", padding: "12px 14px", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-sans)", fontSize: "var(--text-md)", fontWeight: 600, color: page === l.id ? "var(--brand-red)" : "var(--text-strong)" }}>{l.label}</button>
            ))}
            <div style={{ marginTop: 8 }}>
              <Button variant="primary" size="md" style={{ width: "100%" }} onClick={() => navTo("contact")}>{nav.getStarted}</Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
